import React, { FC, useEffect, useRef } from "react";
import useWindowSize from "../../hooks/useWindowSize";
import FilterCategoryTitle from "../filterCategoryTitle";

export type SliderRange = {
  min: number;
  max: number;
};

export type SliderTitle = {
  value: number;
  title: string;
};

type Props = {
  label?: string;
  range: SliderRange;
  selectedRange?: SliderRange;
  setSelectedRange: (newRange: SliderRange) => void;
  displayTitleWidth?: number;
  values?: SliderTitle[];
};

const StyledSelectSlider: FC<Props> = ({
  label: label,
  range: range,
  selectedRange: selectedRange = range,
  setSelectedRange: setSelectedRange,
  displayTitleWidth: displayTitleWidth,
  values: values,
}) => {
  const { min, max } = range;
  const { min: selectedMin, max: selectedMax } = selectedRange;
  const progressBarRef = useRef<HTMLDivElement>(null);

  const { windowWidth } = useWindowSize();

  useEffect(
    function handleProgessBarPosition() {
      if (!progressBarRef.current) return;

      const distanceFromLeft = ((selectedMin - min) / (max - min)) * 100 + "%";
      progressBarRef.current.style.left = distanceFromLeft;

      const distanceFromRight =
        100 - ((selectedMax - min) / (max - min)) * 100 + "%";
      progressBarRef.current.style.right = distanceFromRight;
    },
    [selectedMin, selectedMax, min, max]
  );

  const handleMin = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMin = parseInt(e.target.value);
    if (newMin < selectedMax && newMin >= min)
      setSelectedRange({ min: newMin, max: selectedMax });
    else if (newMin >= selectedMax && newMin < max)
      setSelectedRange({ min: newMin, max: newMin + 1 });
  };

  const handleMax = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMax = parseInt(e.target.value);
    if (newMax > selectedMin && newMax <= max)
      setSelectedRange({ min: selectedMin, max: newMax });
    else if (newMax <= selectedMin && newMax > min)
      setSelectedRange({ min: newMax - 1, max: newMax });
  };

  function getSelectedMinTitle() {
    let title: string = selectedMin.toString();
    if (values)
      values.map((value) => {
        if (value.value == selectedMin) {
          title = value.title;
        }
      });
    return title;
  }

  function getSelectedMaxTitle() {
    let title: string = selectedMax.toString();
    if (values)
      values.map((value) => {
        if (value.value == selectedMax) {
          title = value.title;
        }
      });
    return title;
  }

  const isMobile = windowWidth && windowWidth < 768;

  const displayTitleStyles =
    displayTitleWidth && isMobile && (label === "Height" || label === "Taille")
      ? {
          width: displayTitleWidth * 0.45 + "px",
          minWidth: displayTitleWidth * 0.45 + "px",
          fontSize: "0.60rem",
        }
      : displayTitleWidth
      ? {
          width: displayTitleWidth + "px",
          minWidth: displayTitleWidth + "px",
        }
      : { width: "fit-content" };

  return (
    <div className="StyledSelectSlider">
      {label && <FilterCategoryTitle title={label} />}
      <div className="flex flex-row items-center h-[45px] space-x-1.5">
        <div
          data-testid="select-slider-min"
          style={displayTitleStyles}
          className={`appearance-none px-1 whitespace-nowrap flex justify-center items-center min-w-fit h-full rounded-md border border-custom-gray bg-white text-custom-black`}
        >
          {getSelectedMinTitle()}
        </div>
        <div className="w-full slider relative h-1.5 bg-custom-gray rounded-md">
          <div
            className="absolute h-1.5 bg-custom-orange rounded-md"
            ref={progressBarRef}
          ></div>
          <div className="relative max-w-full flex">
            <input
              onChange={handleMin}
              data-testid="min-input"
              type="range"
              min={min}
              max={max}
              value={selectedMin}
              className="absolute w-full h-1.5 bg-transparent appearance-none pointer-events-none"
            />
            <input
              onChange={handleMax}
              data-testid="max-input"
              type="range"
              min={min}
              max={max}
              value={selectedMax}
              className="absolute w-full h-1.5 bg-transparent appearance-none pointer-events-none"
            />
          </div>
        </div>
        <div
          data-testid="select-slider-max"
          style={displayTitleStyles}
          className={`appearance-none px-1 whitespace-nowrap flex justify-center items-center min-w-fit h-full rounded-md border border-custom-gray bg-white text-custom-black`}
        >
          {getSelectedMaxTitle()}
        </div>
      </div>
    </div>
  );
};

export default StyledSelectSlider;
